import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { ldScript, serviceSchema } from "@/lib/schema";
import heroImgAsset from "@/assets/usf-marshall.jpg.asset.json";
const heroImg = heroImgAsset.url;

export const Route = createFileRoute("/sunsync")({
  head: () => ({
    ...pageHead({
      title: "SUNSYNC™ Solar Sales Platform · SunSmart Engineering",
      description:
        "SUNSYNC™ is our platform for selling, designing and installing solar systems from one dashboard, built for contractors who want to offer turnkey solar.",
      path: "/sunsync",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "SUNSYNC™",
          description:
            "SunSmart's SUNSYNC™ platform for selling, designing, and installing solar systems from a single dashboard.",
          url: "/sunsync",
          serviceType: "Solar Sales & Design Platform",
        }),
      ),
    ],
  }),
  component: SunsyncPage,
});

const WHY = [
  {
    title: "Expert Engineering & Design",
    points: [
      "Speed through permitting with permit-ready designs created by a team with extensive experience in solar engineering and your clients' AHJ.",
      "Our designs are optimized for performance, ensuring compliance with local codes and regulations.",
    ],
  },
  {
    title: "Reliable Procurement",
    points: [
      "We partner with distributors to source and provide high-quality materials at competitive prices.",
      "Benefit from our established relationships with top suppliers, giving you access to the best products in the industry.",
    ],
  },
  {
    title: "Professional Installation",
    points: [
      "Our network of trained professionals ensures installations adhere to strict quality and safety standards.",
      "Every project is executed with the goal of maximizing system performance and long-term reliability.",
    ],
  },
];

const STEPS = [
  {
    n: "01",
    k: "Train & Equip Your Team",
    v: "Live, on-site training teaches reps to qualify, size, and sell solar using the SUNSYNC™ platform. Sales scripts, proposal templates, and financing calculators included.",
  },
  {
    n: "02",
    k: "Contract & Feasibility Review",
    v: "Signed contracts route automatically to SunSmart Engineering. Our engineers verify layout, structural load, interconnection, and profitability. If issues arise, we handle the customer conversation and update the scope.",
  },
  {
    n: "03",
    k: "Bill of Materials & Procurement",
    v: "We generate a complete, cost-optimized BOM. Materials ship through our distributor partners at negotiated best-in-class pricing.",
  },
  {
    n: "04",
    k: "Installer Matchmaking & Project Management",
    v: "SunSmart pairs you with a pre-vetted solar installation partner in the project's territory. We manage plan sets, permits, inspections, and scheduling, so your core business never stalls.",
  },
  {
    n: "05",
    k: "30-Year Peace-of-Mind Warranty (Coming Soon)",
    v: "One warranty, one point of contact. Full parts and labor coverage.",
  },
];

function SunsyncPage() {
  return (
    <PageLayout
      kicker="SUNSYNC™"
      title="A full service solar division at your fingertips."
      intro="Our SUNSYNC™ platform empowers contractors to offer turnkey solar solutions without hiring new crews, re-tooling your workflow, or juggling unfamiliar paperwork. It bridges the gap between contractors, solar installers, and distributors, turning complex commercial and residential PV projects into predictable, profitable jobs."
    >
      <PageSection label="01" title="Trusted, Vetted Installers">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-lg leading-relaxed text-muted-foreground">
              Utilizing our network of highly vetted solar contractors, SUNSYNC™ ensures your installations
              will be handled by some of the best in the industry. Our installers go through regular
              vetting, audits, credit checks, and more, allowing you to have the peace of mind knowing both
              your projects and your customers' investments are handled with the utmost attention and care.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              All of our installers are trusted clients of SunSmart Engineering.
            </p>
            <a
              href="https://www.sunsync.partners/"
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
            >
              Go to platform <span>→</span>
            </a>
          </Reveal>
          <Reveal delay={100} className="lg:col-span-6">
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={heroImg}
                alt="Large-scale commercial solar installation"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </PageSection>

      <PageSection theme="surface" label="02" title="Why Use SUNSYNC™?">
        <div className="grid gap-px border border-border bg-border sm:grid-cols-3">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 100}>
              <div className="h-full bg-background p-8 md:p-10">
                <div className="font-display text-lg font-bold uppercase">{w.title}</div>
                <ul className="mt-4 space-y-3">
                  {w.points.map((p) => (
                    <li key={p} className="text-sm leading-relaxed text-muted-foreground">
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      <PageSection label="03" title="What We Handle (So You Don't Have To)">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            SUNSYNC™ removes every hurdle between your sales team and a signed solar contract. We start by
            training your reps to size, price, and pitch PV systems with our platform-based calculators and
            proposal templates. Once a customer commits, you hand the job to us and get back to selling. Our
            engineers crank out PE-stamped plan sets, create a cost-optimized bill of materials, and submit
            the entire packet for permitting, AHJ approval, and utility interconnection, with no calls to the
            county, no paperwork pileups.
          </p>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground">
            Behind the scenes, we match you with an established, fully vetted solar installation company that
            has the crews, equipment, and insurance already in place, so you never scramble to staff a project
            or worry about workmanship.
          </p>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground">
            From there, SUNSYNC™ works with your installers to coordinate every on-site inspection, oversee
            commissioning, and activate live system monitoring before turning the keys back over to you and
            your customer. Your installer remains the single point of contact for warranty claims,
            rapid-response service calls, and long-term maintenance, shielding your office from post-install
            headaches.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
          >
            Get started <span>→</span>
          </Link>
        </Reveal>
      </PageSection>

      <PageSection theme="surface" label="04" title="How SUNSYNC™ Works">
        <Reveal>
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun">5 Simple Steps</p>
        </Reveal>
        <div className="mt-8 grid gap-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 80}>
              <div className="flex gap-6 rounded-sm border border-border bg-background p-6">
                <div className="font-display text-4xl font-black leading-none text-sun">{s.n}</div>
                <div>
                  <div className="font-display text-lg font-bold uppercase">{s.k}</div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      <PageSection>
        <div className="text-center">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-[clamp(2rem,5vw,4.5rem)] font-black uppercase leading-[0.95]">
              Turn your next proposal into a <span className="text-sun">solar sale.</span>
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              Ready to expand your business with SUNSYNC™? Contact us today to learn how we can support your
              next project.
            </p>
          </Reveal>
          <Reveal delay={160}>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
            >
              Get started <span>→</span>
            </Link>
          </Reveal>
        </div>
      </PageSection>
    </PageLayout>
  );
}
